import {
  contactsCreateMutation,
  contactsPartialUpdateMutation,
  contactsRetrieveOptions,
  contactsRetrieveQueryKey,
  leadsContactsListQueryKey,
  leadsLogsListQueryKey,
  opportunitiesContactsListQueryKey,
  opportunitiesLogsListQueryKey
} from '@crm/api'
import { DuplicateAlert, PhoneInput } from '@crm/components'
import { handleContactMutationError } from '@crm/utils'

import { Text } from '@shared/components'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Button, Col, Flex, Form, Input, Row, Switch, Tooltip } from 'antd'
import { Add, Trash } from 'iconsax-react'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { AddExistingContactModalForm } from './'

type Props = {
  id?: number
  formName: string
  closeModal: () => void
}

export const LeadForm = ({ id, formName, closeModal }: Props) => {
  const { t } = useLocales()
  const { id: leadId, opportunityId } = useParams()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const [form] = Form.useForm()
  const { notification } = App.useApp()

  const [isOpenModalAddExistingContact, setIsOpenModalAddExistingContact] =
    useState(false)

  const { data, isLoading } = useQuery({
    ...contactsRetrieveOptions({ path: { id: Number(id) } }),
    enabled: !!id,
    refetchOnMount: 'always'
  })

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        ...data
      })
    }
  }, [data, form])

  const onMutate = {
    onError: (error) => handleContactMutationError(error, form),
    onSuccess: () => {
      invalidateQueries([
        leadsContactsListQueryKey({ path: { lead_id: String(leadId) } }),
        opportunitiesContactsListQueryKey({
          path: { opportunity_id: String(opportunityId) }
        }),
        leadsLogsListQueryKey({ path: { lead_id: String(leadId) } }),
        opportunitiesLogsListQueryKey({
          path: { opportunity_id: String(opportunityId) }
        }),
        contactsRetrieveQueryKey({ path: { id: Number(id) } })
      ])
      closeModal()
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    }
  }

  const {
    mutate: createMutate,
    error: createError,
    reset: resetCreate
  } = useMutation({
    ...contactsCreateMutation(),
    ...onMutate
  })

  const {
    mutate: editMutate,
    error: editError,
    reset: resetEdit
  } = useMutation({
    ...contactsPartialUpdateMutation(),
    ...onMutate
  })

  const onSubmit = () => {
    const { ...rest } = form.getFieldsValue()

    const requestBody = {
      ...rest,

      lead: leadId ? Number(leadId) : undefined,
      leads: leadId ? [Number(leadId)] : [],
      opportunities: opportunityId ? [Number(opportunityId)] : []
    }

    if (id) {
      editMutate({ path: { id: Number(id) }, body: requestBody })
    } else {
      createMutate({ body: requestBody })
    }
  }

  const onChangePrimaryPhone = (checked: boolean, currentIndex: number) => {
    if (checked) {
      const phoneNumbers = form.getFieldValue('phone_numbers') || []

      phoneNumbers?.forEach((_: unknown, index: number) => {
        if (index !== currentIndex) {
          form.setFieldValue(['phone_numbers', index, 'is_primary'], false)
        }
      })
    }
  }

  const onOpenAddExistingContact = () => setIsOpenModalAddExistingContact(true)
  const onCloseAddExistingContact = (closeParentModal?: boolean) => {
    setIsOpenModalAddExistingContact(false)

    if (closeParentModal) {
      closeModal()
    }
  }

  return (
    <>
      <Form
        name={formName}
        form={form}
        onFinish={onSubmit}
        layout="vertical"
        disabled={isLoading}
      >
        <DuplicateAlert
          error={createError ?? editError}
          onClose={() => {
            resetCreate()
            resetEdit()
          }}
        />

        <Row justify="end" gutter={12}>
          <Col>
            <Button
              type="primary"
              icon={<Add size={20} />}
              onClick={onOpenAddExistingContact}
              hidden={!!id}
              className="mb-20"
            >
              {t('contacts.add_existing_contact')}
            </Button>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="first_name"
              label={t('contacts.first_name')}
              rules={[{ required: true }]}
              required
            >
              <Input placeholder={t('generic.placeholder_input_here')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="last_name" label={t('contacts.last_name')}>
              <Input placeholder={t('generic.placeholder_input_here')} />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item name="email" label={t('contacts.email')}>
              <Input placeholder={t('generic.placeholder_input_here')} />
            </Form.Item>
          </Col>
        </Row>

        <Col span={24}>
          <Form.List name="phone_numbers">
            {(fields, { add, remove }, { errors }) => (
              <Row gutter={[12, 12]} align="middle" justify="space-between">
                <Col span={12}>
                  <Text>{t('contacts.phone_numbers')}</Text>
                </Col>

                <Col span={12}>
                  <Flex justify="end">
                    <Button
                      variant="dashed"
                      color="primary"
                      onClick={() => add()}
                      icon={<Add />}
                    >
                      {t('contacts.add_phone_number')}
                    </Button>
                  </Flex>
                </Col>

                {fields?.map(({ key, name, ...restField }) => (
                  <Col span={12} key={key}>
                    <Flex key={key} gap={8} align="center">
                      <Form.Item
                        {...restField}
                        name={[name, 'number']}
                        rules={[
                          {
                            required: true,
                            message: t(
                              'account_settings.this_field_is_required'
                            )
                          }
                        ]}
                      >
                        <PhoneInput placeholder={t('contacts.phone_number')} />
                      </Form.Item>

                      <Tooltip title={t('contacts.set_primary_number')}>
                        <Form.Item
                          {...restField}
                          name={[name, 'is_primary']}
                          valuePropName="checked"
                        >
                          <Switch
                            onChange={(checked) =>
                              onChangePrimaryPhone(checked, name)
                            }
                          />
                        </Form.Item>
                      </Tooltip>

                      <Form.Item>
                        <Button
                          danger
                          icon={<Trash size={16} />}
                          onClick={() => remove(name)}
                        />
                      </Form.Item>
                    </Flex>
                  </Col>
                ))}

                <Col span={24}>
                  <Text color="colorError">{errors?.join?.(', ')}</Text>
                </Col>
              </Row>
            )}
          </Form.List>
        </Col>
      </Form>

      <AddExistingContactModalForm
        open={isOpenModalAddExistingContact}
        onCancel={onCloseAddExistingContact}
      />
    </>
  )
}
